/**
 * Operations endpoints (bos-api).
 *
 * The path is relative to the axios client's base, which already ends in
 * `/api/v1` — so `/ledger/operations` reaches `…/api/v1/ledger/operations`.
 * Mirrors `AccountsEndpoints`, `JournalEndpoints` and `TrialBalanceEndpoints`,
 * the other live ledger resources.
 *
 * **The catalog read takes no parameters, and that is measured against the
 * contract rather than forgotten.** There is no `locale`: it carries both
 * locales on every row because the Templates tab shows both at once. There is
 * no search, filter, page or sort: seventeen rows on a fresh tenant is a list,
 * not a page, so the tab narrows it client-side where it is instant.
 *
 * The editor's paths key on the template's **`key`**, not its uuid — the key is
 * what the seed file, the API and any support conversation call the operation,
 * so it is what a URL should carry too. It is percent-encoded here because
 * nothing stops an accountant creating one with a slash in it once custom
 * operations land.
 */

export const OperationsEndpoints = {
    /** GET — the whole catalog: the tenant's templates, then the system rows. */
    catalog: () => '/ledger/operations',
    /** POST — mint a brand-new template. Same path as `catalog`, different method. */
    create: () => '/ledger/operations',
    /**
     * GET — this tenant's operation categories, in display order.
     *
     * Its own route rather than a field on the catalog: the editor needs the
     * whole set to offer a choice, and the catalog only carries the ones
     * actually in use.
     */
    categories: () => '/ledger/operations/categories',
    /**
     * POST — add a category of the tenant's own. Same path as `categories`,
     * different method.
     *
     * Answers the **whole list**, not the row it added: the management screen is
     * the list, and a create changes its length.
     */
    createCategory: () => '/ledger/operations/categories',
    /** PUT — rename one, in both languages at once. Answers the whole list. */
    updateCategory: (id: string) =>
        `/ledger/operations/categories/${encodeURIComponent(id)}`,
    /**
     * DELETE — remove one. `204` on success, `409` with the blocking templates
     * counted in `dependents` while any operation is filed under it.
     *
     * Answers no body, unlike the other two — the delete shape every resource in
     * this API shares. The list is a refetch away, and the mutation invalidates
     * it.
     */
    deleteCategory: (id: string) =>
        `/ledger/operations/categories/${encodeURIComponent(id)}`,
    /** GET — one template in full: every version summary, plus active and draft lines. */
    template: (key: string) => `/ledger/operations/${encodeURIComponent(key)}`,
    /** POST — create or replace the template's single draft version. */
    versions: (key: string) =>
        `/ledger/operations/${encodeURIComponent(key)}/versions`,
    /** POST — publish a draft as the version in force from a date. */
    activate: (key: string, version: number) =>
        `/ledger/operations/${encodeURIComponent(key)}/versions/${version}/activate`,
    /** POST — retire an operation, prospectively. */
    block: (key: string) =>
        `/ledger/operations/${encodeURIComponent(key)}/block`,
    /** POST — bring a blocked operation back into use. */
    unblock: (key: string) =>
        `/ledger/operations/${encodeURIComponent(key)}/unblock`,
    /**
     * GET — the operations this session may record on a date.
     *
     * `date` is a query parameter rather than implied, because the answer is a
     * function of it: which version is in force is resolved from the entry's own
     * date, so a form filled for last month belongs to last month's recipe.
     */
    recordable: (date: string) =>
        `/ledger/operations/recordable?date=${encodeURIComponent(date)}`,
    /** POST — the exact lines a record would post, computed and thrown away. */
    preview: (key: string) =>
        `/ledger/operations/${encodeURIComponent(key)}/preview`,
    /** POST — post the operation through the journal. Idempotent on the body's `id`. */
    record: (key: string) =>
        `/ledger/operations/${encodeURIComponent(key)}/record`,
    /**
     * GET — a page of recorded operations, filtered.
     *
     * Built from a `URLSearchParams` rather than a template string because every
     * one of these is optional and an empty one must be *absent*, not sent as
     * `key=`: the server reads a blank key as a filter on the empty string and
     * answers with nothing.
     */
    activity: (params: URLSearchParams) => {
        const query = params.toString();
        return `/ledger/operations/activity${query ? `?${query}` : ''}`;
    },
    /** GET — a template's whole configuration history: versions + change log. */
    history: (key: string) =>
        `/ledger/operations/${encodeURIComponent(key)}/history`,
    /** GET — one recorded operation with the lines it posted. */
    activityDetail: (recordId: string, locale?: string) =>
        `/ledger/operations/activity/${encodeURIComponent(recordId)}${
            locale ? `?locale=${encodeURIComponent(locale)}` : ''
        }`,
};
